import React, { Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Route } from "react-router-dom";

import { BASE_URL, DEACTIVATE_FORGOT_PASSWORD, FORGOT_PASSWORD, RESET_PASSWORD } from "../data/Constants";
import ErrorBoundary from "../pages/ErrorBoundary";
import SignIn from "../pages/Auth/SignIn";
import ForgotPassword from "../pages/Auth/ForgotPassword";
import ResetPassword from "../pages/Auth/ResetPassword";
import DeactivateForgotPassword from "../pages/Auth/DeactivateForgotPassword";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import MasterLayout from "../layouts/MasterLayout";
import InterviewReport from "../pages/Reports/Pages/InterviewReport";

const NewApplicants = React.lazy(() => import("../pages/NewApplicants"));
const NewApplicantsListing = React.lazy(() => import("../pages/NewApplicants/Pages/Listings"));
const ProfileInformation = React.lazy(() => import("../pages/NewApplicants/Pages/ProfileInformation"));
const Appeals = React.lazy(() => import("../pages/Appeals"));
const Rejected = React.lazy(() => import("../pages/Rejected"));
const Successful = React.lazy(() => import("../pages/Successful"));
const RefundCases = React.lazy(() => import("../pages/RefundCases"));
const ATP = React.lazy(() => import("../pages/ATP"));
const AtpProfileInformation = React.lazy(() => import("../pages/ATP/Pages/Profile"));
const AtpListing = React.lazy(() => import("../pages/ATP/Pages/Listings"));
const CpdSubmission = React.lazy(() => import("../pages/CpdSubmission"));
const Activities = React.lazy(() => import("../pages/Activities"));
const ActivitiesListing = React.lazy(() => import("../pages/Activities/Pages/Listings"));
const Dashboard = React.lazy(() => import("../pages/Dashboard"));
const DashboardComponents = React.lazy(() => import("../pages/Dashboard/DashboardComponents"));
const DashboardOverview = React.lazy(() => import("../pages/Dashboard/DashboardComponents/DashboardOverview/DashboardOverview"));
const DashboardViewDetails = React.lazy(() => import("../pages/Dashboard/DashboardViewDetails"));
const DashboardAtp = React.lazy(() => import("../pages/Dashboard/DashboardComponents/DashboardAtp/AtpAnalysisInformation"));
const DashboardTrainingAnalysis = React.lazy(() => import("../pages/Dashboard/DashboardComponents/DashboardTrainingAnalysis/TrainingAnalysisInformation"));
const MemberShipStatus = React.lazy(() => import("../pages/MemberShipStatus"));
const MembershipProfileInformation = React.lazy(() => import("../pages/MemberShipStatus/Pages/ProfileInformation"));
const MembershipListings = React.lazy(() => import("../pages/MemberShipStatus/Pages/Listings"));
const RenewalStatus = React.lazy(() => import("../pages/Renewals"));
const RenewalProfileInformation = React.lazy(() => import("../pages/Renewals/Pages/ProfileInformation"));
const RenewalListings = React.lazy(() => import("../pages/Renewals/Pages/Listings"));
const IncompleteApplications = React.lazy(() => import("../pages/IncompleteApplications"));
const IncompleteApplicationListings = React.lazy(() => import("../pages/IncompleteApplications/Pages/Listings"));
const IncompleteApplicationProfileInformation = React.lazy(() => import("../pages/IncompleteApplications/Pages/ProfileInformation"));
const Reports = React.lazy(() => import("../pages/Reports"));
const NewApplicantReport = React.lazy(() => import("../pages/Reports/Pages/NewApplicants"));
const ExportReport = React.lazy(() => import("../pages/Reports/Pages/ExportReport"));
const ATPReport = React.lazy(() => import("../pages/Reports/Pages/AtpReport"));
const RenewalReport = React.lazy(() => import("../pages/Reports/Pages/RenewalReport"));
const MembershipAnalysis = React.lazy(() => import("../pages/Reports/Pages/MembershipAnalysis"));
const CpdAnalysisReport = React.lazy(() => import("../pages/Reports/Pages/CpdAnalysisReport"));
const CpdHours = React.lazy(() => import("../pages/CPDHours"));
const CPDHoursListing = React.lazy(() => import("../pages/CPDHours/Listings"));
const BulkRegistrations = React.lazy(() => import("../pages/BulkRegistrations"));
const BulkRegistrationsListings = React.lazy(() => import("../pages/BulkRegistrations/Pages/Listings"));
export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path={BASE_URL} errorElement={<ErrorBoundary />}>
            <Route
                index
                element={
                    <PublicRoute>
                        <SignIn />
                    </PublicRoute>
                }
            />
            <Route
                path={FORGOT_PASSWORD}
                element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                }
            />
            <Route
                path={`${RESET_PASSWORD}/:token`}
                element={
                    <PublicRoute>
                        <ResetPassword />
                    </PublicRoute>
                }
            />
            <Route
                path={`${DEACTIVATE_FORGOT_PASSWORD}/:token`}
                element={
                    <PublicRoute>
                        <DeactivateForgotPassword />
                    </PublicRoute>
                }
            />
            <Route
                element={
                    <ProtectedRoute>
                        <MasterLayout />
                    </ProtectedRoute>
                }
            >
                <Route
                    path="dashboard"
                    element={
                        <Suspense>
                            <Dashboard />
                        </Suspense>
                    }
                >
                    <Route index path="" element={<DashboardOverview />} />
                    <Route path="atp-analysis" element={<DashboardAtp />} />
                    <Route path="training-analysis" element={<DashboardTrainingAnalysis />} />
                    <Route path="view-details" element={<DashboardViewDetails />} />
                </Route>

                <Route
                    path="new-applicants"
                    element={
                        <Suspense>
                            <NewApplicants />
                        </Suspense>
                    }
                >
                    <Route index element={<NewApplicantsListing />} />
                    <Route path=":applicant_id/:id" element={<ProfileInformation />} />
                </Route>

                <Route
                    path="incomplete-applicants"
                    element={
                        <Suspense>
                            <IncompleteApplications />
                        </Suspense>
                    }
                >
                    <Route index element={<IncompleteApplicationListings />} />
                    <Route path=":id" element={<IncompleteApplicationProfileInformation />} />
                </Route>

                <Route
                    path="rejected"
                    element={
                        <Suspense>
                            <Rejected />
                        </Suspense>
                    }
                />

                <Route
                    path="successful"
                    element={
                        <Suspense>
                            <Successful />
                        </Suspense>
                    }
                />

                {/* Refund Cases */}
                <Route
                    path="refund-cases"
                    element={
                        <Suspense>
                            <RefundCases />
                        </Suspense>
                    }
                />

                {/* CPD Submission */}
                <Route
                    path="cpd-submission"
                    element={
                        <Suspense>
                            <CpdSubmission />
                        </Suspense>
                    }
                />

                {/* Appeals */}
                <Route
                    path="appeals"
                    element={
                        <Suspense>
                            <Appeals />
                        </Suspense>
                    }
                />

                {/* ATP */}
                <Route
                    path="atp"
                    element={
                        <Suspense>
                            <ATP />
                        </Suspense>
                    }
                >
                    <Route index element={<AtpListing />} />
                    <Route path=":referenceId/:id" element={<AtpProfileInformation />} />
                </Route>

                {/* CPD Submissions */}
                <Route
                    path="cpd-submissions"
                    element={
                        <Suspense>
                            <NewApplicants />
                        </Suspense>
                    }
                >
                    <Route index element={<NewApplicantsListing />} />
                    <Route path=":applicant_id/:id" element={<ProfileInformation />} />
                </Route>

                {/* Membership Status */}
                <Route
                    path="membership-status"
                    element={
                        <Suspense>
                            <MemberShipStatus />
                        </Suspense>
                    }
                >
                    <Route index element={<MembershipListings />} />
                    <Route path=":applicant_id/:id" element={<MembershipProfileInformation />} />
                </Route>

                {/* Renewals */}
                <Route
                    path="renewals"
                    element={
                        <Suspense>
                            <RenewalStatus />
                        </Suspense>
                    }
                >
                    <Route index element={<RenewalListings />} />
                    <Route path=":applicant_id/:id" element={<RenewalProfileInformation />} />
                </Route>

                {/* ATPs */}
                <Route
                    path="atps"
                    element={
                        <Suspense>
                            <NewApplicants />
                        </Suspense>
                    }
                >
                    <Route index element={<NewApplicantsListing />} />
                    <Route path=":applicant_id/:id" element={<ProfileInformation />} />
                </Route>

                {/* Report */}
                <Route
                    path="new-application-report"
                    element={
                        <Suspense>
                            <Reports />
                        </Suspense>
                    }
                ></Route>
                {/* Activities */}
                <Route
                    path="activities"
                    element={
                        <Suspense>
                            <Activities />
                        </Suspense>
                    }
                >
                    <Route index element={<ActivitiesListing />} />
                </Route>
                {/* Report */}
                <Route
                    path=""
                    element={
                        <Suspense>
                            <Reports />
                        </Suspense>
                    }
                >
                    <Route path="/reports/new-applicants" element={<NewApplicantReport />} />
                    <Route path="/reports/atps" element={<ATPReport />} />
                    <Route path="/reports/renewals" element={<RenewalReport />} />
                    <Route path="/reports/membership-analysis" element={<MembershipAnalysis />} />
                    <Route path="/reports/cpd-analysis" element={<CpdAnalysisReport />} />
                    <Route path="/reports/export-reports" element={<ExportReport />} />
                    <Route path="/reports/interview-scheduled" element={<InterviewReport />} />
                </Route>
                {/* CPD Hours */}
                <Route
                    path="cpd-hours"
                    element={
                        <Suspense>
                            <CpdHours />
                        </Suspense>
                    }
                >
                    <Route index element={<CPDHoursListing />} />
                </Route>
                {/* Bulk Registrations */}
                <Route
                    path="bulk-registrations"
                    element={
                        <Suspense>
                            <BulkRegistrations />
                        </Suspense>
                    }
                >
                    <Route index element={<BulkRegistrationsListings />} />
                </Route>
            </Route>
        </Route>
    )
);
